import { CHALLENGE } from "../../content/eventsArchive";
import { Page } from "../../layout/Page";
import { Card } from "../../ui/Card";
import { Reveal } from "../../ui/Reveal";
import { Section } from "../../ui/Section";

function Points({ items }: { items: readonly string[] }) {
  return (
    <ul className="mt-4 space-y-2">
      {items.map((p) => (
        <li key={p} className="flex gap-3 text-ink-soft">
          <span aria-hidden="true" className="text-teal-deep">
            &middot;
          </span>
          <span>{p}</span>
        </li>
      ))}
    </ul>
  );
}

function FaqItem({
  question,
  answer,
}: {
  question: string;
  answer: string | null;
}) {
  return (
    <details className="group rounded-lg border border-hairline bg-card transition-colors hover:border-hairline-strong">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 font-display text-base text-ink marker:content-none">
        <span>{question}</span>
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="shrink-0 text-teal-deep transition-transform duration-200 group-open:rotate-180"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </summary>
      <div className="max-w-prose whitespace-pre-line px-5 pb-5 text-sm leading-relaxed text-ink-soft">
        {answer ?? "Details to follow."}
      </div>
    </details>
  );
}

export function ChallengePage() {
  return (
    <Page title="Meditation Challenge" heading="Global Meditation Challenge">
      <Section id="editions" eyebrow="Events">
        <div className="grid gap-6 md:grid-cols-2">
          {CHALLENGE.editions.map((edition) => (
            <Reveal key={edition.title}>
              <Card
                title={edition.title}
                image={{ src: edition.banner, alt: `${edition.title} banner` }}
                cta={{
                  label: "Know More",
                  href: edition.href,
                  external: edition.external,
                }}
              >
                <p>{edition.dates}</p>
                <p className="mt-1">{edition.workshops} workshops</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section id="2022" tone="ivory-deep">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <h2 className="font-display rule-heading text-[1.85rem] text-ink md:text-[2.5rem]">
              {CHALLENGE.what.title}
            </h2>
            <p className="mt-4 max-w-prose text-ink-soft">
              {CHALLENGE.what.body}
            </p>
            <Points items={CHALLENGE.what.points} />
          </div>
          <div>
            <h2 className="font-display rule-heading text-[1.85rem] text-ink md:text-[2.5rem]">
              {CHALLENGE.forMe.title}
            </h2>
            <Points items={CHALLENGE.forMe.points} />
          </div>
        </div>
      </Section>

      <Section
        id="faq"
        title="Frequently Asked Questions"
        lede="Registration for the 2022 challenge is closed."
      >
        <div className="flex flex-col gap-3">
          {CHALLENGE.faq.map((item) => (
            <FaqItem
              key={item.question}
              question={item.question}
              answer={item.answer}
            />
          ))}
        </div>
      </Section>

      <Section id="results" title={CHALLENGE.results.title} tone="ivory-deep">
        <ul className="grid gap-4 sm:grid-cols-3">
          {CHALLENGE.results.posters.map((p) => (
            <li key={p.src}>
              <img
                src={p.src}
                alt={p.alt}
                className="aspect-[3/4] w-full rounded-lg border border-hairline bg-card object-contain"
                loading="lazy"
              />
            </li>
          ))}
        </ul>
        <ul className="mt-6 max-w-prose space-y-2 text-sm text-ink-soft">
          {CHALLENGE.results.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      </Section>

      <Section id="recap" title="Watch the recap">
        <div className="aspect-video w-full overflow-hidden rounded-lg border border-hairline">
          <iframe
            className="h-full w-full"
            src={CHALLENGE.youtube}
            title="30-day Global Meditation Challenge"
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </Section>
    </Page>
  );
}
